import { WorklistItemStatus } from 'generated/graphql';
import { ReportStatus, type ReportStatuses } from 'domains/reporter/Reporter/state';
import { formatStudyInformation } from 'common/StudyInformationCard/StudyInformationCard';
import type { GroupableOrGroupedItem, ListItem } from './types';

const getClaimedById = (item: GroupableOrGroupedItem): string | null => {
  if ('claimedBy' in item) {
    const claimed = item.claimedBy as unknown;
    if (claimed == null) {
      return null;
    }
    if (typeof claimed === 'string') {
      return claimed;
    }
    if (typeof claimed === 'object' && claimed !== null && 'id' in claimed) {
      return (claimed as { id?: string | null }).id ?? null;
    }
  }
  return null;
};

const getGroupId = (item: GroupableOrGroupedItem): string | null => {
  if ('groupId' in item) {
    const groupId = (item as { groupId?: string | null }).groupId;
    return groupId ?? null;
  }
  return null;
};

export const toListItem = (item: GroupableOrGroupedItem): ListItem => ({
  smid: item.smid,
  studyDescription: item.studyDescription,
  studyDate: item.studyDate,
  accessionNumber: item.accessionNumber,
  claimedBy: getClaimedById(item),
  groupId: getGroupId(item),
});

export const formatImageCountLabel = (imageCount?: number | null): string => {
  const count = imageCount ?? 0;
  return `${count} Images`;
};

export const formatLinkCasesPrimaryText = ({
  studyDescription,
  accessionNumber,
  studyDate,
  imageCount,
  showImageCount,
}: {
  studyDescription: Nullable<string>;
  accessionNumber: Nullable<string>;
  studyDate: Nullable<Date>;
  imageCount?: number | null;
  showImageCount: boolean;
}): string => {
  const baseText = formatStudyInformation(studyDescription, accessionNumber, studyDate);
  if (!showImageCount) {
    return baseText;
  }

  return `${baseText} | ${formatImageCountLabel(imageCount)}`;
};

export const getLinkCasesWarning = ({
  item,
  myId,
  currentGroupId,
}: {
  item: ListItem;
  myId: Nullable<string>;
  currentGroupId: Nullable<string>;
}): string | null => {
  if (item.claimedBy != null && item.claimedBy !== myId) {
    return 'Exam cannot be linked because it is already claimed.';
  }

  if (item.groupId != null && item.groupId !== currentGroupId) {
    return 'Exam cannot be linked because it is already in another case group.';
  }

  return null;
};

type ApolloErrorLike = {
  graphQLErrors?: ReadonlyArray<{ message?: unknown }>;
  message?: unknown;
};

const getApolloErrorMessage = (error: unknown): string | null => {
  if (error == null || typeof error !== 'object') {
    return null;
  }

  const apolloError = error as ApolloErrorLike;
  // Prefer the resolver's message over Apollo's transport-level wrapper message.
  const graphQLErrorMessage = (
    Array.isArray(apolloError.graphQLErrors) ? apolloError.graphQLErrors : []
  ).find(({ message }) => typeof message === 'string' && message !== '')?.message;

  if (typeof graphQLErrorMessage === 'string') {
    return graphQLErrorMessage;
  }

  return typeof apolloError.message === 'string' && apolloError.message !== ''
    ? apolloError.message
    : null;
};

const CLAIMED_BY_ERROR_PATTERN = /due to \(claimed_by=([^)]+)\)/;

export const getLinkCasesErrorDialogText = (error: unknown): string => {
  const claimedByEmail = getApolloErrorMessage(error)?.match(CLAIMED_BY_ERROR_PATTERN)?.[1];

  if (claimedByEmail != null) {
    return `This exam can't be linked because it is already claimed by ${claimedByEmail}.`;
  }

  return 'There was a network error while linking exams. Please retry.';
};

export const isLinkCasesActionDisabled = ({
  reportStatus,
  worklistItemStatus,
}: {
  reportStatus: ReportStatuses;
  worklistItemStatus: Nullable<WorklistItemStatus>;
}): boolean => {
  return reportStatus !== ReportStatus.Idle || worklistItemStatus === WorklistItemStatus.Pending;
};

const LINK_CASES_BLOCKED_VOICE_ACTIONS = ['SubmitReport', 'DraftReport', 'DiscardReport'] as const;
const LINK_CASES_BLOCKED_SHORTCUT_ACTIONS = [
  'submitReport',
  'draftReport',
  'discardReport',
] as const;
const LINK_CASES_BLOCKED_EXTENSION_ACTIONS = [
  'reporter-submit-report',
  'reporter-discard-report',
] as const;

export const isLinkCasesBlockedVoiceAction = (action: string): boolean =>
  LINK_CASES_BLOCKED_VOICE_ACTIONS.includes(
    action as (typeof LINK_CASES_BLOCKED_VOICE_ACTIONS)[number]
  );

export const isLinkCasesBlockedShortcutAction = (action: string): boolean =>
  LINK_CASES_BLOCKED_SHORTCUT_ACTIONS.includes(
    action as (typeof LINK_CASES_BLOCKED_SHORTCUT_ACTIONS)[number]
  );

export const isLinkCasesBlockedExtensionShortcutDetail = (detail: string): boolean =>
  LINK_CASES_BLOCKED_EXTENSION_ACTIONS.includes(
    detail as (typeof LINK_CASES_BLOCKED_EXTENSION_ACTIONS)[number]
  );
